import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, Form, Input, Space, Typography } from "antd";
import { LockOutlined } from "@ant-design/icons";
import AuthShell from "components/auth/AuthShell";
import AuthBrand from "components/auth/AuthBrand";
import useAccountRecovery from "hooks/auth/useAccountRecovery";
import paths from "routes/paths";

const { Title, Text, Paragraph } = Typography;

const AuthRecoverReset = () => {
  const navigate = useNavigate();
  const { resetPassword, isLoading } = useAccountRecovery();
  const [redirecting, setRedirecting] = useState(false);

  const submit = async (values) => {
    const res = await resetPassword(values.password, values.confirm);
    if (res?.success) {
      setRedirecting(true);
    }
  };

  useEffect(() => {
    if (!redirecting) return;

    const timer = window.setTimeout(() => {
      navigate(paths.simpleLogin, {
        replace: true,
        state: {
          recoveryNotice:
            "Your password was updated successfully. Redirecting you to sign in...",
        },
      });
    }, 1500);

    return () => window.clearTimeout(timer);
  }, [navigate, redirecting]);

  return (
    <AuthShell>
      <Card
        variant="borderless"
        style={{
          width: "100%",
          maxWidth: 520,
          borderRadius: 28,
          boxShadow: "0 24px 70px rgba(15, 23, 42, 0.10)",
          border: "1px solid rgba(226,232,240,0.95)",
          background: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(18px)",
        }}
        styles={{ body: { padding: 32 } }}
      >
        <div style={{ marginBottom: 24, textAlign: "center" }}>
          <AuthBrand centered size={96} />
        </div>
        <Space vertical size={8} style={{ width: "100%" }}>
          <Text
            style={{
              textTransform: "uppercase",
              letterSpacing: "0.14em",
              color: "#64748b",
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            Create new password
          </Text>
          <Title level={2} style={{ margin: 0, color: "#111827" }}>
            Choose a new password
          </Title>
          <Paragraph style={{ marginBottom: 0, color: "#475569" }}>
            Pick a secure password for your account and confirm it below.
          </Paragraph>
        </Space>

        {redirecting && (
          <div style={{ marginTop: 20 }}>
            <Card
              size="small"
              style={{
                borderRadius: 14,
                borderColor: "#bbf7d0",
                background: "#f0fdf4",
              }}
            >
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <Text strong style={{ color: "#166534" }}>
                  Password updated
                </Text>
                <Text style={{ color: "#166534" }}>
                  Redirecting you to sign in...
                </Text>
              </Space>
            </Card>
          </div>
        )}

        <Form layout="vertical" requiredMark={false} onFinish={submit} style={{ marginTop: 24 }}>
          <Form.Item
            label="New password"
            name="password"
            rules={[{ required: true, message: "Enter a new password" }]}
          >
              <Input.Password
                prefix={<LockOutlined style={{ color: "#94a3b8" }} />}
                placeholder="New password"
                disabled={isLoading || redirecting}
                size="large"
              />
          </Form.Item>

          <Form.Item
            label="Confirm password"
            name="confirm"
            rules={[{ required: true, message: "Confirm your new password" }]}
          >
              <Input.Password
                prefix={<LockOutlined style={{ color: "#94a3b8" }} />}
                placeholder="Confirm password"
                disabled={isLoading || redirecting}
                size="large"
              />
          </Form.Item>

          <Space style={{ width: "100%", justifyContent: "space-between" }} wrap>
            <Button onClick={() => navigate(paths.simpleLogin)} disabled={isLoading}>
              Back to sign in
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={isLoading || redirecting}
              style={{ backgroundColor: "#22c55e", borderColor: "#22c55e" }}
            >
              Reset password
            </Button>
          </Space>
        </Form>
      </Card>
    </AuthShell>
  );
};

export default AuthRecoverReset;
